<template>
  <div>
    <div class="tw-bg-white tw-shadow tw-border-t tw-border-gray-200">
      <base-content class="tw-pt-3 tw-pb-3.5">
        <base-breadcrumbs :breadcrumbs="breadcrumbs" />
      </base-content>
      <base-content class="tw-flex tw-justify-between tw-items-center tw-pt-0">
        <h1 class="tw-text-xl sm:tw-text-2xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin">Manage Program Price Change Requests</h1>
      </base-content>
    </div>
    <base-content>
      <base-text-field v-model="query" ref="searchInput" class="tw-w-full" placeholder="Search for a Program" size="sm" tabindex="1">
        <template #prefix>
          <search-icon class="tw-h-5 tw-w-5" />
        </template>
      </base-text-field>
      <div class="tw-flex tw-justify-end tw-w-full">
        <base-button type="flat" class="tw-mt-3" size="sm" @click="openFilters()">
          <template #icon>
            <filter-icon />
          </template>
          Filters
        </base-button>
      </div>

      <base-table :loading="isLoading" :fields="fields" :items="items" class="tw-mt-4">
        <template #locationName="{item}">
          <div class="tw-whitespace-nowrap">{{ item.businessLocationName }}</div>
        </template>

        <template #programName="{item}">
          <div class="tw-whitespace-nowrap">{{ item.businessProgramName }}</div>
        </template>

        <template #priceSuggestions="{item}">
          <div class="tw-flex tw-flex-col">
            <template v-if="item.priceSuggestion">
              <div v-for="(suggestion, index) in parsePriceSuggestions(item.priceSuggestion)" :key="index" class="tw-whitespace-nowrap">
                {{ suggestion.programGroup }}: ${{ formatCentsToDisplay(suggestion.changePrice) }}
              </div>
            </template>
            <div v-else class="tw-text-gray-500 tw-italic">N/A</div>
          </div>
        </template>

        <template #status="{item}">
          <div class="tw-whitespace-nowrap">{{ capitalizeFirstLetter(item.status) }}</div>
        </template>

        <template #submitted="{item}">
          <div class="tw-flex tw-flex-col">
            <div class="tw-text-gray-500">{{ formatDate(item.insertedDate) }}</div>
            <div class="tw-whitespace-nowrap">{{ capitalizeFirstLetter(item.insertedBy) }}</div>
          </div>
        </template>

        <template #actions="{item}">
          <div class="tw-flex tw-items-center tw-space-x-2">
            <base-button type="flat" @click="viewDetails(item)">
              <template v-slot:icon>
                <list-icon /> </template
              >Details
            </base-button>
          </div>
        </template>

        <template #empty-state>
          <div class="tw-flex tw-justify-center tw-py-5">
            No Price Change Requests Found
          </div>
        </template>

        <template #loading-row>
          <td class="tw-py-3 tw-px-4">
            <base-skeleton-loader class="tw-h-3 tw-w-40" />
          </td>
          <td class="tw-py-3 tw-px-4">
            <base-skeleton-loader class="tw-h-3 tw-w-32" />
          </td>
          <td class="tw-py-3 tw-px-4">
            <div class="tw-flex tw-flex-col">
              <base-skeleton-loader class="tw-h-3 tw-w-28 tw-mb-1" />
              <base-skeleton-loader class="tw-h-3 tw-w-24" />
            </div>
          </td>
          <td class="tw-py-3 tw-px-4">
            <base-skeleton-loader class="tw-h-3 tw-w-16" />
          </td>
          <td class="tw-py-3 tw-px-4">
            <div class="tw-flex tw-flex-col">
              <base-skeleton-loader class="tw-h-3 tw-w-20" />
              <base-skeleton-loader class="tw-h-3 tw-w-24 tw-mt-1" />
            </div>
          </td>
          <td class="tw-py-3 tw-px-4">
            <base-skeleton-loader class="tw-h-8 tw-w-18" />
          </td>
        </template>
      </base-table>
      <base-pagination v-model="page" class="tw-mt-2" />
    </base-content>

    <business-price-change-request-approve-slideover
      ref="priceChangeRequestSlideover"
      :business-id="businessId"
      :programs="allPrograms"
      :plans="plans"
      @request-approved="refresh()"
      @request-rejected="refresh()"
    />
    <business-program-price-change-filter ref="priceChangeFilterSlideover" :business-id="businessId" :allPrograms="allPrograms" @filter-updated="applyFilter" />
  </div>
</template>

<script>
import BusinessPriceChangeRequestApproveSlideover from "@/views/business/program-price-chaneges/components/BusinessLocationPriceChangeRequestApproveSlideover";
import BusinessProgramPriceChangeFilter from "@/views/business/program-price-chaneges/components/BusinessProgramPriceChangeFilter";
import { mapGetters, mapState } from "vuex";
import { ListIcon, SearchIcon, FilterIcon } from "@/assets/svgs";
import { debounce } from "debounce";
import { isEqual } from "lodash";
import moment from "moment";

const TAKE_KEY = "predictile_price_change_requests_take";

export default {
  name: "ProgramPriceChangeRequests",
  components: {
    BusinessPriceChangeRequestApproveSlideover,
    ListIcon,
    SearchIcon,
    FilterIcon,
    BusinessProgramPriceChangeFilter,
  },
  props: {
    businessId: String,
  },
  data() {
    return {
      items: [],
      page: {
        take: localStorage.getItem(TAKE_KEY) ? +localStorage.getItem(TAKE_KEY) : 50,
        skip: 0,
        total: 0,
      },
      fields: [
        { title: "Location", key: "locationName", thClass: "" },
        { title: "Program Name", key: "programName", thClass: "" },
        { title: "Price Suggestions", key: "priceSuggestions", thClass: "" },
        { title: "Status", key: "status", thClass: "" },
        { title: "Submitted", key: "submitted", thClass: "" },
        { title: "Actions", key: "actions", thClass: "tw-w-0 tw-text-center" },
      ],
      query: null,
      isLoading: false,
      filters: {
        locationsFilter: [],
        programsFilter: [],
        statusFilter: [],
      },
      allPrograms: [],
      plans: [],
    };
  },
  computed: {
    ...mapGetters("role", ["isLocationFiltered"]),
    ...mapState("organization", ["organization"]),

    breadcrumbs() {
      return [{ title: "Settings", onClick: () => this.$router.push(`/businesses/${this.businessId}/business`) }, { title: "Manage Program Price Change Requests" }];
    },
  },
  created() {
    this.loadPage();
    this.getPrograms();
    this.getPlans();
    this.debouncedLoadPage = debounce(this.loadPage, 500);
  },
  watch: {
    page(newValue) {
      localStorage.setItem(TAKE_KEY, newValue.take);
      this.loadPage();
    },
    query() {
      this.page.skip = 0;
      this.debouncedLoadPage();
    },
  },
  methods: {
    openFilters() {
      this.$refs.priceChangeFilterSlideover.show(this.currentFilters);
    },

    formatCentsToDisplay(cents) {
      if (typeof cents !== "number" || Number.isNaN(cents)) {
        return "0.00";
      }
      return (cents / 100).toFixed(2);
    },

    parsePriceSuggestions(jsonString) {
      try {
        return JSON.parse(jsonString);
      } catch (e) {
        console.error("Error parsing priceSuggestionJson:", e);
        return [];
      }
    },

    formatDate(date) {
      if (date) return new moment.utc(date).local().format("M/D/YYYY h:mm a");
    },
    capitalizeFirstLetter(str) {
      if (!str) return "";
      return str.charAt(0).toUpperCase() + str.slice(1);
    },

    viewDetails(item) {
      this.$refs.priceChangeRequestSlideover.show(item);
    },

    refresh() {
      this.loadPage();
    },

    loadPage() {
      this.isLoading = true;

      let filters = { locationsFilter: [], programFilter: [], statusFilter: [] };
      if (this.filters !== undefined) filters = this.filters;

      let url = `/businesses/${this.businessId}/request-program-price-changes`;
      url += `?skip=${this.page.skip}&take=${this.page.take}`;
      if (this.query) url += `&query=${encodeURIComponent(this.query)}`;

      this.$axios
        .post(url, {
          locationsFilter: filters.locationsFilter,
          statusFilter: filters.statusFilter,
          programFilter: filters.programFilter,
        })
        .then((res) => {
          this.items = res.data.items;
          this.page.take = res.data.take;
          this.page.skip = res.data.skip;
          this.page.total = res.data.total;
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: `Failed to load price change requests`,
            message: `We had a problem retrieving the list of price change requests. Please reload the page to try again.`,
            error: err,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    getPrograms() {
      try {
        this.$axios.get(`/businesses/${this.businessId}/programs/list?includeDeleted=false`).then((res) => {
          this.allPrograms = res.data;
        });
      } catch (error) {
        console.error("Error fetching programs:", error);
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Failed to load programs",
          message: "Unable to load the list of programs for filtering.",
          error,
        });
      }
    },
    applyFilter(filters) {
      if (!isEqual(this.filters, filters)) {
        this.filters = filters;
        this.loadPage();
      } else {
        this.filters = filters;
      }
    },
    getPlans() {
      try {
        this.$axios.get(`/businesses/${this.businessId}/plans/list`).then((res) => {
          this.plans = res.data;
        });
      } catch (error) {
        console.error("Error fetching plan:", error);
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Failed to load plan",
          message: "Unable to load the list of plans.",
          error,
        });
      }
    },
    next() {
      this.page.skip += this.page.take;
    },
    previous() {
      this.page.skip -= this.page.take;
    },
  },
};
</script>
